'use client';

import { c as _c } from "react/compiler-runtime";
import { useDotEnv } from '@repo/dot-env/client';
import { useQuery } from '@repo/tanstack/query';
import { expandGoogleMapsLink } from './expand-google-maps-link.ts';
import { getPlaceDetails } from './get-place-details.ts';
import { type Coordinates, parseGoogleMapsInput } from './google-maps-input.ts';
import { canSearchExactGoogleMapsLabel, type GoogleLocationResult, useExactGoogleLocationSearch } from './use-search-location.ts';
export type PastedLocationResult = {
  coordinates: Coordinates;
  kind: 'pinned-coordinates';
} | {
  coordinates?: Coordinates;
  googlePlace: GoogleLocationResult;
  kind: 'google-location';
};
type UsePastedGoogleMapsLocationOptions = {
  disabled?: boolean;
  query: string;
  resolvePlaceDetails?: typeof getPlaceDetails;
};
const pinnedLocation = (coordinates?: Coordinates): null | PastedLocationResult => coordinates ? {
  coordinates,
  kind: 'pinned-coordinates'
} : null;

/** Picks the exact Google result nearest the pasted pin, or keeps the pin when none is close. */
const resolvePastedPlace = async ({
  coordinates,
  googlePlaces,
  resolvePlaceDetails,
  serviceUrl
}: {
  coordinates?: Coordinates;
  googlePlaces: GoogleLocationResult[];
  resolvePlaceDetails: typeof getPlaceDetails;
  serviceUrl?: string;
}): Promise<null | PastedLocationResult> => {
  const results = await Promise.all(googlePlaces.map(async googlePlace => {
    const details = await resolvePlaceDetails(googlePlace.place_id, serviceUrl);
    return details.status === 'found' ? {
      googlePlace,
      location: details.value.address
    } : null;
  }));
  const candidates = results.filter(candidate => candidate !== null);
  if (candidates.length !== results.length) return pinnedLocation(coordinates);
  const squaredDistance = ({
    location
  }: (typeof candidates)[number]) => coordinates ? (location.lat - coordinates.lat) ** 2 + (location.lng - coordinates.lng) ** 2 : 0;
  const closestCandidate = candidates.reduce((closest, candidate) => squaredDistance(candidate) < squaredDistance(closest) ? candidate : closest);
  return !coordinates || squaredDistance(closestCandidate) <= 0.0001 ? {
    ...(coordinates ? {
      coordinates
    } : {}),
    googlePlace: closestCandidate.googlePlace,
    kind: 'google-location'
  } : {
    coordinates,
    kind: 'pinned-coordinates'
  };
};
export const usePastedGoogleMapsLocation = t0 => {
  const $ = _c(5);
  const {
    disabled: t1,
    query,
    resolvePlaceDetails: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const resolvePlaceDetails = t2 === undefined ? getPlaceDetails : t2;
  const {
    GEOLOCATION_SERVICE_URL
  } = useDotEnv();
  const directMapsInput = parseGoogleMapsInput(query);
  const shortLinkUrl = directMapsInput?.kind === "short-link" ? directMapsInput.url : null;
  const {
    data: expandedMapsInput,
    isPending: isMapsExpansionPending
  } = useQuery({
    enabled: shortLinkUrl !== null,
    queryFn: () => expandGoogleMapsLink(shortLinkUrl ?? "", GEOLOCATION_SERVICE_URL),
    queryKey: ["google-maps-short-link", GEOLOCATION_SERVICE_URL, shortLinkUrl],
    retry: false,
    staleTime: Infinity
  });
  const isExpandingMapsInput = shortLinkUrl !== null && isMapsExpansionPending;
  const mapsInput = shortLinkUrl !== null && expandedMapsInput ? parseGoogleMapsInput(expandedMapsInput) : directMapsInput;
  const mapsLink = mapsInput?.kind === "maps-link" ? mapsInput : null;
  const mapsLabel = mapsLink?.label;
  const coordinates = mapsInput?.kind === "short-link" ? undefined : mapsInput?.coordinates;
  const {
    data: exactGoogleResults,
    isFetching: isExactGoogleFetching
  } = useExactGoogleLocationSearch({
    coordinates: mapsLink?.coordinates,
    enabled: !disabled && !!mapsLabel,
    label: mapsLabel
  });
  const canMatchLabel = !!mapsLabel && canSearchExactGoogleMapsLabel(mapsLabel);
  const isWaitingForExactResults = canMatchLabel && (isExactGoogleFetching || exactGoogleResults === undefined);
  const googlePlaces = canMatchLabel && !isExactGoogleFetching && exactGoogleResults !== undefined && exactGoogleResults.length > 0 && exactGoogleResults.length <= 3 && (!!coordinates || exactGoogleResults.length === 1) ? exactGoogleResults : null;
  const {
    data: resolvedPlace,
    isPending: isPlacePending
  } = useQuery({
    enabled: googlePlaces !== null,
    queryFn: () => resolvePastedPlace({
      coordinates,
      googlePlaces: googlePlaces ?? [],
      resolvePlaceDetails,
      serviceUrl: GEOLOCATION_SERVICE_URL
    }).catch(() => pinnedLocation(coordinates)),
    queryKey: ["google-maps-pasted-place", GEOLOCATION_SERVICE_URL, coordinates?.lat ?? null, coordinates?.lng ?? null, googlePlaces?.map(_temp) ?? null],
    retry: false,
    staleTime: Infinity
  });
  const isResolvingPastedLocation = isWaitingForExactResults || googlePlaces !== null && isPlacePending;
  const getPastedLocation = () => {
    if (!mapsInput || mapsInput.kind === "short-link" || isWaitingForExactResults) {
      return null;
    }
    if (googlePlaces) {
      return resolvedPlace ?? null;
    }
    return pinnedLocation(coordinates);
  };
  const t3 = isExactGoogleFetching || isExpandingMapsInput || isResolvingPastedLocation;
  const t4 = directMapsInput !== null;
  const t5 = getPastedLocation();
  let t6;
  if ($[0] !== mapsLabel || $[1] !== t3 || $[2] !== t4 || $[3] !== t5) {
    t6 = {
      isLoading: t3,
      isPastedInput: t4,
      mapsLabel,
      pastedLocation: t5
    };
    $[0] = mapsLabel;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  return t6;
};
function _temp(googlePlace) {
  return googlePlace.place_id;
}